import * as React from "react"
import { FiSmile } from "react-icons/fi"
import { Icon } from "../../components/common/icons/"
import { useBoolean } from "../useBoolean"
import { Flex } from "@chakra-ui/react"
import { useClickOutside } from "../useClickOutside"
import { ChatStyles } from "../../components/servicesPanel/chat/index"
import { useBrowserType } from "../useBrowserType"
import { blendWithWhiteBackground } from "../../util"

interface ReactionItemProps {
  emodji: string
  total: string | number
  handleAddReaction: (
    evennt: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void
  handleRemoveReaction: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void
  isAddedReaction?: boolean
  styleschat: ChatStyles
  uniqKey: string
  isMobile: boolean
}

export const useReactions = (
  handleReactionCallback: (emodji: string) => void,
  emodjiList: string[][],
  toggleModalCallback?: () => void
) => {
  const { isWindows } = useBrowserType()

  const modalWrapperRef = React.useRef<HTMLDivElement>(null)
  const [isOpen, { off, toggle }] = useBoolean(false)

  const handleOutsideClick = React.useCallback((): void => {
    if (isOpen) {
      off()
    }
  }, [isOpen, off])

  const toggleModal = React.useCallback(
    (e): void => {
      toggle()

      toggleModalCallback()
      e.stopPropagation()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useClickOutside(modalWrapperRef, handleOutsideClick)

  const handleReaction = React.useCallback(
    (emodji: string): void => {
      handleReactionCallback(emodji)
      off()
    },
    [handleReactionCallback, off]
  )

  return {
    ReactionsTrigger: ({ styleschat }: { styleschat: ChatStyles }) => (
      <Icon
        src={FiSmile}
        cursor="pointer"
        opacity={0.75}
        color={styleschat?.servicePanelElementPrimaryText ?? "#A0AEC0"}
        _hover={{ opacity: 1 }}
        onClick={e => toggleModal(e)}
      />
    ),
    ReactionsModal: ({
      styleschat,
      isMobile,
      customStyles
    }: {
      styleschat: ChatStyles
      isMobile?: boolean
      customStyles?: React.CSSProperties
    }) => (
      <>
        {isOpen && (
          <Flex
            backgroundColor={
              blendWithWhiteBackground(
                styleschat?.servicePanelElementModalBackground
              ) ?? "white"
            }
            pos={"absolute"}
            bottom={"-45px"}
            left={"-2px"}
            zIndex={1000000000}
            ref={modalWrapperRef}
            fontSize={isMobile ? "1.4rem" : isWindows ? "1.4rem" : "1.7rem"}
            borderRadius="0.375rem"
            borderColor={styleschat?.servicePanelElementBorder ?? "#E2E8F0"}
            borderWidth="2px"
            width={"100%"}
            style={{ ...customStyles }}
          >
            {emodjiList.map(item => (
              <Flex
                key={`emodji-${item[1]}`}
                cursor="pointer"
                onClick={() => handleReaction(item[0])}
                alignContent="center"
                justifyContent="center"
                _hover={{
                  backgroundColor:
                    styleschat?.accessButtonMouseoverColor ?? "#edf2f7"
                }}
                flexGrow="1"
                _first={{
                  borderBottomLeftRadius: "0.25rem",
                  borderTopLeftRadius: "0.25rem"
                }}
                _last={{
                  borderBottomRightRadius: "0.25rem",
                  borderTopRightRadius: "0.25rem"
                }}
              >
                {item[1]}
              </Flex>
            ))}
          </Flex>
        )}
      </>
    ),
    ReactionItem: ({
      emodji,
      total,
      handleAddReaction,
      handleRemoveReaction,
      isAddedReaction,
      styleschat,
      uniqKey,
      isMobile
    }: ReactionItemProps) => (
      <Flex
        key={uniqKey}
        backgroundColor={
          isAddedReaction
            ? styleschat?.servicePanelElementBorder ?? "gray.200"
            : styleschat?.secondaryAccessButtonBorderColor ?? "white"
        }
        flexDirection="row"
        fontSize="0.8rem"
        borderRadius="0.8rem"
        borderColor={
          isAddedReaction
            ? styleschat?.servicePanelElementBorder ?? "#E2E8F0"
            : styleschat?.secondaryAccessButtonBackgroundColor ?? "#E2E8F0"
        }
        borderWidth="2px"
        alignItems="center"
        alignContent="center"
        justifyContent="center"
        padding="0 1px"
        marginRight={"5px"}
        marginTop="5px"
        cursor={isAddedReaction ? "unset" : "pointer"}
        onClick={!isAddedReaction ? handleAddReaction : handleRemoveReaction}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        color={
          isAddedReaction
            ? styleschat?.servicePanelElementBorderOffset
            : styleschat?.secondaryAccessButtonBackgroundColor
        }
        _hover={
          !isAddedReaction && !isMobile && {
            backgroundColor: styleschat?.secondaryAccessButtonMouseoverColor,
            color: styleschat?.servicePanelElementBorderOffset
          }
        }
      >
        <Flex
          marginLeft="6px"
          marginRight="4px"
          alignItems="center"
          alignContent="center"
          justifyContent="center"
        >
          {emodji}
        </Flex>
        <Flex
          marginRight="6px"
          alignItems="center"
          alignContent="center"
          justifyContent="center"
        >
          {total}
        </Flex>
      </Flex>
    ),
    isOpenReplyModal: isOpen
  }
}
